import { apiVersions, getLogicEndpoint } from '@/services/endpoints';
import { isLocalMode } from '@/services/localRuntimeClient';
import { getArmBaseUrl } from '@/services/armConfig';
import { CONNECTION_REFERENCE_SETTING_PREFIX } from '@/utils/connections/serviceProviderAppSettings';
import { isFeatureEnabled } from '@/featureFlags';

// =============================================================================
// HELPERS
// =============================================================================

/** In local mode, route to mock ARM service; otherwise use the real logic endpoint. */
const getBaseUrl = (): string => (isLocalMode() ? getArmBaseUrl() : getLogicEndpoint());

/**
 * The draft environment-settings slot is gated behind the `enableDraftSettings`
 * feature flag. When the flag is off, every read/write targets the production
 * slot regardless of the caller's `isDraftMode` — this is the single choke
 * point that keeps all draft-settings behavior behind the flag.
 */
const shouldUseDraftSlot = (isDraftMode: boolean): boolean =>
  isDraftMode && isFeatureEnabled('enableDraftSettings');

/**
 * Build the environmentSettings/default resource URL. Path segments are
 * `encodeURIComponent`-escaped because project / app names can contain
 * characters that are reserved in URLs (spaces, `+`, etc.); without this,
 * the request would silently target the wrong resource.
 */
const buildSettingsUrl = (projectName: string, appName: string, isDraftMode: boolean): string =>
  `${getBaseUrl()}/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(appName)}/environmentSettings/${shouldUseDraftSlot(isDraftMode) ? 'draft' : 'default'}?api-version=${apiVersions.environmentSettings}`;

/**
 * Builds a sanitized error for API failures. We deliberately omit the
 * response body from `Error.message` because env-settings payloads can
 * echo user keys/values, and this message is later passed to
 * `telemetryService.trackException`, which does not sanitize it.
 * Structured fields (`status`, optional `requestId`) carry the diagnostic
 * signal instead.
 */
const buildApiError = (
  op: 'fetch' | 'update' | 'addFlag',
  projectName: string,
  appName: string,
  status: number,
  requestId?: string,
): ApiErrorWithStatus => {
  const verb = op;
  const err: ApiErrorWithStatus = new Error(
    `Failed to ${verb} environment settings for ${projectName}/${appName}: HTTP ${status}`,
  );
  err.status = status;
  if (requestId) err.requestId = requestId;
  return err;
};

// =============================================================================
// TYPES
// =============================================================================

/**
 * Marker type for fetch-derived errors that carry the HTTP status code.
 * Consumers (hooks, telemetry) can inspect `.status` to categorize failures
 * without parsing free-form error messages.
 */
export interface ApiErrorWithStatus extends Error {
  status?: number;
  /** Server-provided request/correlation id, when available. Safe for telemetry. */
  requestId?: string;
}

export interface EnvironmentSetting {
  name: string;
  value: string | null;
  slotSetting?: boolean;
}

export interface EnvironmentSettingsResponse {
  properties: {
    settings: EnvironmentSetting[];
  };
}

export interface EnvironmentSettingsPutPayload {
  properties: {
    settings: Array<{
      name: string;
      value: string;
    }>;
  };
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * App-setting name that gates draft environment settings. When present on the
 * production settings with value `'true'`, the app reads/writes a separate
 * draft slot.
 */
const DRAFT_ENVIRONMENT_SETTINGS_ENABLED = 'Workflows.DraftEnvironmentSettingsEnabled';

/**
 * Determines whether draft environment settings are enabled for an app by
 * inspecting its production settings for the `Workflows.DraftEnvironmentSettingsEnabled`
 * flag. Returns `true` only when that setting is present and its value is the
 * string `'true'` (case-insensitive); otherwise `false`.
 */
export const isDraftSettingsEnabled = (prodSettings: EnvironmentSetting[]): boolean =>
  prodSettings.some(
    (setting) =>
      setting.name === DRAFT_ENVIRONMENT_SETTINGS_ENABLED &&
      setting.value?.toLowerCase() === 'true',
  );

/**
 * Returns a copy of the production settings with the draft-enabled flag set to
 * `'true'`. Any stale flag entry is dropped first so the flag appears exactly
 * once. Used to optimistically reflect the flag the runtime just wrote to the
 * production slot without re-fetching (an immediate GET after the write can
 * return stale data while the ACA container re-deploys).
 */
export const withDraftSettingsEnabled = (prodSettings: EnvironmentSetting[]): EnvironmentSetting[] => [
  ...prodSettings.filter((setting) => setting.name !== DRAFT_ENVIRONMENT_SETTINGS_ENABLED),
  { name: DRAFT_ENVIRONMENT_SETTINGS_ENABLED, value: 'true' },
];

/**
 * Fetches the environment settings for a single slot (draft or production).
 * Returns the parsed settings array, or the raw 404 response so the caller can
 * decide whether to fall back to another slot.
 */
const fetchSettingsForSlot = async (
  projectName: string,
  appName: string,
  isDraftMode: boolean,
  token: string,
): Promise<EnvironmentSetting[]> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!isLocalMode()) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(buildSettingsUrl(projectName, appName, isDraftMode), {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    // Response body is intentionally NOT included in the Error to avoid
    // leaking env-var values into telemetry. See buildApiError for details.
    throw buildApiError(
      'fetch',
      projectName,
      appName,
      response.status,
      response.headers?.get('x-ms-request-id') ?? undefined,
    );
  }

  const data: EnvironmentSettingsResponse = await response.json();
  return data.properties.settings;
};

/**
 * Fetches environment settings for a given application.
 * Returns the array of settings from the response envelope.
 *
 * In draft mode, if the draft slot has no settings yet the runtime returns a
 * 404. In that case we transparently fall back to the production
 * (`default`) slot so the caller sees the effective settings the app would
 * run with rather than an error.
 */
export const getEnvironmentSettings = async (
  projectName: string,
  appName: string,
  isDraftMode: boolean,
  token: string
): Promise<EnvironmentSetting[]> => {
  try {
    return await fetchSettingsForSlot(projectName, appName, isDraftMode, token);
  } catch (err) {
    if (isDraftMode && (err as ApiErrorWithStatus)?.status === 404) {
      return fetchSettingsForSlot(projectName, appName, false, token);
    }
    throw err;
  }
};

/**
 * Updates environment settings for a given application via PUT.
 *
 * **Replace semantics.** The backend uses PUT to replace the entire
 * `properties.settings` collection. Callers must therefore send the *full*
 * desired collection — settings absent from the payload are deleted. See
 * `buildPutSettings` in `@/utils/settings`.
 */
export const updateEnvironmentSettings = async (
  projectName: string,
  appName: string,
  isDraftMode: boolean,
  token: string,
  settings: EnvironmentSettingsPutPayload,
  prodSettings: EnvironmentSetting[],
): Promise<EnvironmentSettingsResponse> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!isLocalMode()) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Only bootstrap the draft-settings feature gate for writes that actually
  // target the draft slot. Bootstrapping on a production write would enable
  // the flag on production and then immediately have the production PUT
  // below (which has replace semantics and doesn't include the flag) undo it.
  if (shouldUseDraftSlot(isDraftMode) && !isDraftSettingsEnabled(prodSettings)) {
    // Re-read production settings immediately before the PUT. The `prodSettings`
    // passed in by the caller may be stale, and this PUT has replace semantics —
    // basing it on a stale snapshot would clobber any production settings that
    // changed since the caller last read them.
    const freshProdSettings = await fetchSettingsForSlot(projectName, appName, /* isDraftMode */ false, token);

    // The flag may already be present on the fresh copy (e.g. a concurrent
    // write enabled it); if so, skip the production write entirely.
    if (!isDraftSettingsEnabled(freshProdSettings)) {
      // Add/update the flag: drop any stale entry, then append it set to 'true'.
      // PUT has replace semantics, so we send the full production collection.
      const prodSettingsToUpdate: EnvironmentSettingsPutPayload = {
        properties: {
          settings: withDraftSettingsEnabled(freshProdSettings).map((setting) => ({
            name: setting.name,
            value: setting.value ?? '',
          })),
        },
      };
      const prodSettingsResponse = await fetch(buildSettingsUrl(projectName, appName, /* isDraftMode */false), {
        method: 'PUT',
        headers,
        body: JSON.stringify(prodSettingsToUpdate),
      });

      if (!prodSettingsResponse.ok) {
        // Response body is intentionally NOT included in the Error to avoid
        // leaking env-var values into telemetry. See buildApiError for details.
        throw buildApiError(
          'addFlag',
          projectName,
          appName,
          prodSettingsResponse.status,
          prodSettingsResponse.headers?.get('x-ms-request-id') ?? undefined,
        );
      }
    }
  }

  const response = await fetch(buildSettingsUrl(projectName, appName, isDraftMode), {
    method: 'PUT',
    headers,
    body: JSON.stringify(settings),
  });

  if (!response.ok) {
    // Response body is intentionally NOT included in the Error to avoid
    // leaking env-var values into telemetry. See buildApiError for details.
    throw buildApiError(
      'update',
      projectName,
      appName,
      response.status,
      response.headers?.get('x-ms-request-id') ?? undefined,
    );
  }

  return response.json();
};

/**
 * Merge `additions` into the current settings collection and PUT the result.
 * The backend replaces `properties.settings` wholesale, so any incremental
 * write must first read + merge to avoid clobbering unrelated keys. Existing
 * keys named in `additions` are overwritten; all others are preserved.
 */
export const upsertEnvironmentSettings = async (
  projectName: string,
  appName: string,
  isDraftMode: boolean,
  token: string,
  additions: Record<string, string>,
  prodSettings: EnvironmentSetting[],
): Promise<EnvironmentSetting[]> => {
  const result = await upsertEnvironmentSettingsWithSnapshot(
    projectName,
    appName,
    isDraftMode,
    token,
    additions,
    prodSettings,
  );
  return result.settings;
};

export interface EnvironmentSettingsUpsertResult {
  settings: EnvironmentSetting[];
  previousSettings: EnvironmentSetting[];
}

export interface EnvironmentSettingsRollbackRequest {
  writtenSettings: Record<string, string>;
  previousSettings: EnvironmentSetting[];
}

/**
 * Upsert settings and return the fresh pre-write collection used for the
 * merge. Callers that coordinate a second persistence step can use this exact
 * snapshot to restore settings if that later step fails.
 */
export const upsertEnvironmentSettingsWithSnapshot = async (
  projectName: string,
  appName: string,
  isDraftMode: boolean,
  token: string,
  additions: Record<string, string>,
  prodSettings: EnvironmentSetting[],
): Promise<EnvironmentSettingsUpsertResult> => {
  const existing = await getEnvironmentSettings(
    projectName,
    appName,
    isDraftMode,
    token,
  );
  if (Object.keys(additions).length === 0) {
    return { settings: existing, previousSettings: existing };
  }

  const merged = new Map<string, string>();
  for (const { name, value } of existing) merged.set(name, value ?? '');
  for (const [name, value] of Object.entries(additions)) merged.set(name, value);

  const response = await updateEnvironmentSettings(projectName, appName, isDraftMode, token, {
    properties: {
      settings: Array.from(merged, ([name, value]) => ({ name, value })),
    },
  }, prodSettings);
  return {
    settings: response.properties.settings,
    previousSettings: existing,
  };
};

/**
 * Revert only settings written by a failed multi-step transaction.
 *
 * The current collection is fetched again before the rollback. A key is
 * restored or removed only when its current value still equals the value this
 * transaction wrote; concurrent edits to that key and unrelated settings are
 * preserved.
 */
export const rollbackEnvironmentSettingChanges = async (
  projectName: string,
  appName: string,
  isDraftMode: boolean,
  token: string,
  request: EnvironmentSettingsRollbackRequest,
  prodSettings: EnvironmentSetting[],
): Promise<EnvironmentSetting[]> => {
  const current = await getEnvironmentSettings(
    projectName,
    appName,
    isDraftMode,
    token,
  );
  const currentByName = new Map(
    current.map((setting) => [setting.name, setting]),
  );
  const previousByName = new Map(
    request.previousSettings.map((setting) => [setting.name, setting]),
  );
  let changed = false;

  for (const [name, writtenValue] of Object.entries(request.writtenSettings)) {
    const currentSetting = currentByName.get(name);
    if (!currentSetting || (currentSetting.value ?? '') !== writtenValue) {
      continue;
    }
    const previousSetting = previousByName.get(name);
    if (previousSetting) {
      currentByName.set(name, previousSetting);
    } else {
      currentByName.delete(name);
    }
    changed = true;
  }

  if (!changed) {
    return current;
  }

  const response = await updateEnvironmentSettings(
    projectName,
    appName,
    isDraftMode,
    token,
    {
      properties: {
        settings: Array.from(currentByName.values(), ({ name, value }) => ({
          name,
          value: value ?? '',
        })),
      },
    },
    prodSettings,
  );
  return response.properties.settings;
};

// =============================================================================
// PUBLISH-TIME DRAFT -> PRODUCTION PROMOTION
// =============================================================================

/**
 * Merges the draft settings slot into the production slot for publish.
 *
 * Semantics (never deletes — pure union):
 *   - Start from the full production collection.
 *   - Add every draft-only setting (present in draft, absent in prod).
 *   - On a name collision, PRODUCTION wins — EXCEPT for connection-reference
 *     settings (names beginning with `connectionreference_`), where the DRAFT
 *     value wins. Connection secrets are authored in draft and must flow to
 *     production on publish; all other shared keys stay as production has them.
 *
 * Production insertion order is preserved; draft-only settings are appended.
 */
export const mergeDraftIntoProd = (
  prodSettings: EnvironmentSetting[],
  draftSettings: EnvironmentSetting[],
): EnvironmentSetting[] => {
  const byName = new Map<string, EnvironmentSetting>();
  for (const setting of prodSettings) byName.set(setting.name, setting);

  for (const setting of draftSettings) {
    const existing = byName.get(setting.name);
    if (existing === undefined) {
      // Draft-only setting — add it.
      byName.set(setting.name, setting);
    } else if (setting.name.startsWith(CONNECTION_REFERENCE_SETTING_PREFIX)) {
      // Shared connection-reference setting — draft wins.
      byName.set(setting.name, setting);
    }
    // Otherwise a shared non-connection setting — keep production (skip).
  }

  return Array.from(byName.values());
};

/**
 * Promotes the draft environment-settings slot into production during publish.
 *
 * Reads both slots, merges them via {@link mergeDraftIntoProd} (draft wins only
 * for `connectionreference_` keys, never deletes), and PUTs the result to the
 * production slot. No-op when:
 *   - draft settings are not enabled for the app (feature gate absent from
 *     production), or
 *   - the draft slot does not exist yet (404) — nothing to promote.
 *
 * @returns `true` when production settings were actually written (the app will
 *   restart to pick them up), `false` when the call was a no-op. Callers use
 *   this to decide whether to wait for the app to become ready again before
 *   issuing further deploy calls.
 */
export const promoteDraftSettingsToProduction = async (
  projectName: string,
  appName: string,
  token: string,
): Promise<boolean> => {
  // Draft settings are gated behind the portal feature flag. With the flag off
  // there is no draft slot to promote.
  if (!isFeatureEnabled('enableDraftSettings')) return false;

  const prodSettings = await fetchSettingsForSlot(projectName, appName, /* isDraftMode */ false, token);

  // The draft-settings feature gate lives on the production slot. If it's not
  // enabled there is no draft slot to promote.
  if (!isDraftSettingsEnabled(prodSettings)) return false;

  let draftSettings: EnvironmentSetting[];
  try {
    draftSettings = await fetchSettingsForSlot(projectName, appName, /* isDraftMode */ true, token);
  } catch (err) {
    // No draft slot yet — nothing to merge into production.
    if ((err as ApiErrorWithStatus)?.status === 404) return false;
    throw err;
  }

  const merged = mergeDraftIntoProd(prodSettings, draftSettings);

  // `updateEnvironmentSettings` targets the production slot (isDraftMode=false).
  // The flag is already enabled (guarded above), so its flag-write branch is a
  // no-op and this performs a single replace PUT of the merged collection.
  await updateEnvironmentSettings(
    projectName,
    appName,
    /* isDraftMode */ false,
    token,
    {
      properties: {
        settings: merged.map((setting) => ({ name: setting.name, value: setting.value ?? '' })),
      },
    },
    prodSettings,
  );

  return true;
};
